import { AxiosInstance, AxiosResponse } from "axios";
import queryString from "query-string";
import {
  IPFilter,
  IpWhiteList,
  IOrganizationDetails,
  IRetentionDetails,
  IOrganizationProps,
  IOrganizationTable,
  IOrgIntegration,
  IAllIntegrationTypes,
} from "./types";
import { ApiResponse } from "../../interfaces/Api";
import { IAdvancedData } from "../../../pages/Organizations/AdvancedSettings/AdvancedSettings";

export interface NestedOrganizationProps {
  item: IOrganizationProps;
  children: NestedOrganizationProps[];
}

interface IFetchParams {
  search?: string;
  page?: number;
  pageSize?: number;
  order?: string;
  parentId?: number;
}

interface ITrees {
  total: number;
  pages: number;
  pageSize: number;
  page: number;
  organizations: NestedOrganizationProps[];
}

const getOrganizationNames = (
  ids: string,
  authenticatedRequest: AxiosInstance
) => authenticatedRequest.get<string[]>(`/api/v2/organizations/names/${ids}`);

const getOrganizationTrees = (
  params: {
    search?: string;
    parentId?: number | string;
    includeParent?: boolean;
    page?: number;
    pageSize?: number;
    getRoot?: boolean;
    nested?: boolean;
  },
  authenticatedRequest: AxiosInstance,
  signal?: AbortSignal
) => {
  const settings = queryString.stringify(params);

  return authenticatedRequest.get<ITrees>(
    `/api/v2/organizations/trees?${settings}`,
    { signal }
  );
};

const getOrganizationsTable = (
  params: IFetchParams,
  authenticatedRequest: AxiosInstance
) => {
  const settings = queryString.stringify({ ...params, order: "DFS" });

  return authenticatedRequest.get<IOrganizationTable>(
    `/api/v2/organizations/table?${settings}`
  );
};

const getOrganization = (
  organizationId: string,
  authenticatedRequest: AxiosInstance
) =>
  authenticatedRequest.get<any>(
    `/api/v2/organizations/${organizationId}/details`
  );

const createOrganization = (
  newOrganizationData: IOrganizationDetails,
  authenticatedRequest: AxiosInstance
) =>
  authenticatedRequest.post<number>(
    `/api/v2/organizations`,
    JSON.stringify(newOrganizationData),
    {
      headers: { "Content-Type": "application/json" },
    }
  );

const deleteOrganization = (
  Id: string | number,
  authenticatedRequest: AxiosInstance
) => authenticatedRequest.delete(`/api/v2/organizations/${Id}`);

const updateOrganization = async (
  objectId: string,
  organizationDetails: IOrganizationDetails,
  authenticatedRequest: AxiosInstance
) =>
  authenticatedRequest.put<{
    data: {
      changedAttributes: IOrganizationDetails;
    };
  }>(`/api/v2/organizations/${objectId}`, JSON.stringify(organizationDetails), {
    headers: { "Content-Type": "application/json" },
  });

const updateRetentionData = async (
  id: string | number,
  authenticatedRequest: AxiosInstance,
  retentionValues: IRetentionDetails
) => {
  await authenticatedRequest.put<ApiResponse<IRetentionDetails>>(
    `/api/v2/organizations/${id}/retention`,
    JSON.stringify(retentionValues),
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

const getRetentionData = (
  id: string | number,
  authenticatedRequest: AxiosInstance
) => authenticatedRequest.get(`/api/v2/organizations/${id}/retention`);

const getRetentionOptions = (authenticatedRequest: AxiosInstance) =>
  authenticatedRequest.get(`/api/v2/organizations/retention`);

const getSettings = (
  id: string | number,
  authenticatedRequest: AxiosInstance
): Promise<AxiosResponse<IAdvancedData[]>> =>
  authenticatedRequest.get<IAdvancedData[]>(
    `/api/v2/organizations/${id}/settings`
  );

const changeSettings = async (
  authenticatedRequest: AxiosInstance,
  orgId: number,
  data: {
    identifier: string;
    value: string | number;
  }
) => {
  await authenticatedRequest.put(
    `/api/v2/organizations/${orgId}/settings`,
    JSON.stringify(data),
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

const getOrganizationsIPList = async (
  orgId: string,
  authenticatedRequest: AxiosInstance
) =>
  authenticatedRequest.get<IpWhiteList>(
    `/api/v2/organizations/${orgId}/ipfilter`
  );

const updateIpAddress = async (
  orgId: string | number,
  data: IPFilter,
  authenticatedRequest: AxiosInstance,
  idToEdit?: number
) => {
  const isEdit = idToEdit ? `/${idToEdit}` : "";
  return authenticatedRequest[!idToEdit ? "post" : "put"]<IpWhiteList>(
    `/api/v2/organizations/${orgId}/ipfilter${isEdit}`,
    JSON.stringify(data),
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

const deleteIpAddress = async (
  orgId: string,
  idToDelete: number | string,
  authenticatedRequest: AxiosInstance
) =>
  authenticatedRequest.delete(
    `/api/v2/organizations/${orgId}/ipfilter/${idToDelete}`
  );

const getIntegrationTypes = async (authenticatedRequest: AxiosInstance) =>
  authenticatedRequest.get<IAllIntegrationTypes>(`/api/planning/integrations`);

const getOrganizationIntegrations = async (
  orgId: string | number,
  authenticatedRequest: AxiosInstance
) =>
  await authenticatedRequest.get<IOrgIntegration[]>(
    `/api/planning/organizations/${orgId}/integration`
  );

const createIntegration = async (
  orgId: string,
  authenticatedRequest: AxiosInstance,
  data: IOrgIntegration
) => {
  await authenticatedRequest.post(
    `/api/planning/organizations/${orgId}/integration`,
    JSON.stringify(data),
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

const updateIntegration = async (
  orgId: string,
  integrationId: string,
  authenticatedRequest: AxiosInstance,
  data: IOrgIntegration
) => {
  await authenticatedRequest.put(
    `/api/planning/organizations/${orgId}/integration/${integrationId}`,
    JSON.stringify(data),
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

const deleteIntegration = async (
  orgId: string,
  integrationId: string,
  authenticatedRequest: AxiosInstance
) =>
  await authenticatedRequest.delete(
    `/api/planning/organizations/${orgId}/integration/${integrationId}`
  );

export {
  deleteOrganization,
  getOrganizationTrees,
  createOrganization,
  getOrganization,
  updateOrganization,
  getRetentionData,
  getRetentionOptions,
  updateRetentionData,
  getSettings,
  changeSettings,
  getOrganizationsIPList,
  updateIpAddress,
  deleteIpAddress,
  getOrganizationsTable,
  getOrganizationIntegrations,
  updateIntegration,
  createIntegration,
  deleteIntegration,
  getIntegrationTypes,
  getOrganizationNames,
};
